/**
 * Growth Prediction Focus - Field Data Configuration
 * Version 2 - Prediction Focus = Growth
 */

import { FieldData } from "./FTDConfig";

/**
 * Lookup object for Growth prediction focus fields
 * Key = Field name exactly as it appears in your table
 */
export const GROWTH_FIELDS: Record<string, FieldData> = {
  Growth: {
    type: "Formula",
    formula: "[Active Users / Last Full Month Active Users] - 1",
    affectedFields: "",
    formulaFields: "Active Users",
  },

  "Active Users": {
    type: "Formula",
    formula: "Old Users + Acquired Users",
    affectedFields: "Growth, Net Rev",
    formulaFields: "Old Users, Acquired Users",
  },

  "Old Users": {
    type: "Formula",
    formula: "(Last Full Month Active Users) * (1 - Churn Rate)",
    affectedFields: "Active Users",
    formulaFields: "Churn Rate, Active Users",
  },

  "Acquired Users": {
    type: "Formula",
    formula: "FTD * (Acquired Users / FTD Ratio)",
    affectedFields: "Active Users",
    formulaFields: "FTD, Acquired Users / FTD Ratio",
  },

  "Churn Rate": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Old Users",
  },

  FTD: {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Acquired Users, RS PV, CPA Income, Clicks, CAC",
  },

  "Acquired Users / FTD Ratio": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Acquired Users",
  },

  "Average Net Revenue Per Active User": {
    type: "User input",
    glossaryText: "By Default, Average of the last 6 full months",
    affectedFields: "Net Rev",
  },

  "Estimated Net Revenue": {
    type: "Formula",
    formula:
      "Average Net Revenue Per Active User * Active Users * [RS BR to NG Active Ratio]",
    affectedFields: "Estimated Revenue Share",
    formulaFields:
      "Average Net Revenue Per Active User, Active Users, RS BR to NG Active Ratio",
  },

  "Average RS %": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Estimated Revenue Share",
  },

  "RS BR to NG Active Ratio": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Net Rev",
  },

  "Estimated Revenue Share": {
    type: "Formula",
    formula: "Average Net Revenue * Average RS percentage",
    affectedFields: "RS PV, Total Income, EPC RS",
    formulaFields: "Estimated Net Revenue, Average RS %",
  },

  "RS PV": {
    type: "Formula",
    formula: "Estimated RS / FTD",
    affectedFields: "",
    formulaFields: "Estimated Revenue Share, FTD",
  },

  "CPA PV": {
    type: "User input",
    glossaryText: "By Default, Last full month value",
    affectedFields: "Estimated CPA Income",
  },

  "Estimated CPA Income": {
    type: "Formula",
    formula: "CPA PV * FTD",
    affectedFields: "Total Income, EPC CPA",
    formulaFields: "CPA PV, FTD",
  },

  "Total Income": {
    type: "Formula",
    formula: "Estimated RevShare + Estimated CPA Income",
    affectedFields: "Spend",
    formulaFields: "Estimated Revenue Share, Estimated CPA Income",
  },

  "Conversion %": {
    type: "User input",
    glossaryText: "By Default, Average of the last 6 full months",
    affectedFields: "Clicks",
  },

  Clicks: {
    type: "Formula",
    formula: "FTD / Conversion Percentage",
    affectedFields: "EPC CPA, EPC RS, CPC",
    formulaFields: "FTD, Conversion %",
  },

  "EPC CPA": {
    type: "Formula",
    formula: "Estimated CPA Income / Clicks",
    affectedFields: "EPC",
    formulaFields: "Estimated CPA Income, Clicks",
  },

  "EPC RS": {
    type: "Formula",
    formula: "Estimated Revenue Share / Clicks",
    affectedFields: "EPC",
    formulaFields: "Estimated Revenue Share, Clicks",
  },

  EPC: {
    type: "Formula",
    formula: "EPC CPA + EPC RS",
    affectedFields: "",
    formulaFields: "EPC CPA, EPC RS",
  },

  CPC: {
    type: "Formula",
    formula: "Spend / Clicks",
    affectedFields: "",
    formulaFields: "Spend, Clicks",
  },

  CAC: {
    type: "Formula",
    formula: "Spend / FTD",
    affectedFields: "",
    formulaFields: "Spend, FTD",
  },

  Spend: {
    type: "Formula",
    formula: "Total Income - Profit",
    affectedFields: "CPC, CAC",
    formulaFields: "Total Income, Profit",
  },

  Profit: {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Spend",
  },
};

/**
 * Field Names Constants
 */
export const FIELD_NAMES = {
  GROWTH: "Growth",
  ACTIVE_USERS: "Active Users",
  OLD_USERS: "Old Users",
  ACQUIRED_USERS: "Acquired Users",
  CHURN_RATE: "Churn Rate",
  FTD: "FTD",
  ACQUIRED_TO_FTD: "Acquired Users / FTD Ratio",
  AVG_NET_REV: "Average Net Revenue Per Active User",
  NET_REVENUE: "Estimated Net Revenue",
  AVG_RS_P: "Average RS %",
  RS_BR_NG: "RS BR to NG Active Ratio",
  ESTIMATED_REVENUE_SHARE: "Estimated Revenue Share",
  RS_PV: "RS PV",
  CPA_PV: "CPA PV",
  CPA_INCOME: "Estimated CPA Income",
  TOTAL_INCOME: "Total Income",
  CVR: "Conversion %",
  CLICKS: "Clicks",
  EPC_CPA: "EPC CPA",
  EPC_RS: "EPC RS",
  EPC: "EPC",
  CPC: "CPC",
  CAC: "CAC",
  SPEND: "Spend",
  PROFIT: "Profit",
  NRPU: "NRPU",
} as const;

/**
 * Helper function to get Growth field info
 * This matches the pattern used in FTDConfig.ts with getFieldInfo()
 */
export const getGrowthFieldInfo = (fieldName: string): FieldData | null => {
  return GROWTH_FIELDS[fieldName] || null;
};
